// Unified data layer for Daily Reports.
// When Supabase is configured, we talk to Postgres via supabase-js.
// Otherwise we delegate to dailyReportsDemo (localStorage) so the preview
// still works end-to-end.

import { supabase, isSupabaseConfigured } from "./supabase";
import {
  DEMO_CHILDREN,
  DEMO_CLASSROOMS,
  DEMO_TEACHER_CLASSROOM_IDS,
  demoAddEntry,
  demoDeleteEntry,
  demoGetReport,
  demoListAllReports,
  demoListReportsForChild,
  demoUpsertReport,
  getChildrenForClassroom,
  getChildrenForParent,
} from "./dailyReportsDemo";

// -------------------------------------------------------------------------
// Classrooms assigned to the currently-signed-in teacher (via staff_assignments)
// -------------------------------------------------------------------------
export async function listTeacherClassrooms(teacherProfileId) {
  if (!isSupabaseConfigured) {
    return DEMO_CLASSROOMS.filter((c) =>
      DEMO_TEACHER_CLASSROOM_IDS.includes(c.id),
    );
  }
  const today = new Date().toISOString().slice(0, 10);
  const { data, error } = await supabase
    .from("staff_assignments")
    .select("classroom:classrooms(id, name, age_group)")
    .eq("profile_id", teacherProfileId)
    .lte("starts_on", today)
    .or(`ends_on.is.null,ends_on.gte.${today}`);
  if (error) throw error;
  return (data || []).map((row) => row.classroom).filter(Boolean);
}

export async function listChildrenInClassroom(classroomId) {
  if (!isSupabaseConfigured) return getChildrenForClassroom(classroomId);
  const baseCols = "id, first_name, last_name, classroom_id, family_id, organization_id, allergies";
  let { data, error } = await supabase
    .from("children")
    .select(`${baseCols}, profile_photo_path`)
    .eq("classroom_id", classroomId)
    .order("first_name", { ascending: true });
  if (error && (error.code === "42703" || /column .* does not exist/i.test(error.message || ""))) {
    ({ data, error } = await supabase
      .from("children")
      .select(baseCols)
      .eq("classroom_id", classroomId)
      .order("first_name", { ascending: true }));
  }
  if (error) throw error;
  return data || [];
}

export async function listChildrenForParent(parentProfileId) {
  if (!isSupabaseConfigured) return getChildrenForParent(parentProfileId);
  // Two-step: PostgREST cannot infer a direct relationship between
  // `children` and `family_members` (both FK to `families`). Query the
  // parent's family_members first, then children by family_id.
  const { data: memberships, error: mErr } = await supabase
    .from("family_members")
    .select("family_id")
    .eq("profile_id", parentProfileId);
  if (mErr) throw mErr;
  const familyIds = [...new Set((memberships || []).map((m) => m.family_id))];
  if (familyIds.length === 0) return [];
  const baseCols = "id, first_name, last_name, classroom_id, family_id, organization_id";
  let { data, error } = await supabase
    .from("children")
    .select(`${baseCols}, profile_photo_path, classroom:classrooms(id, name, location_id, compliance_program_key)`)
    .in("family_id", familyIds)
    .order("first_name", { ascending: true });
  if (error && (error.code === "42703" || /column .* does not exist/i.test(error.message || ""))) {
    ({ data, error } = await supabase
      .from("children")
      .select(`${baseCols}, profile_photo_path, classroom:classrooms(id, name)`)
      .in("family_id", familyIds)
      .order("first_name", { ascending: true }));
  }
  if (error) throw error;
  // Defensive dedupe by id — overlapping family memberships must never
  // surface the same child twice.
  const seen = new Set();
  return (data || []).filter((c) => {
    if (seen.has(c.id)) return false;
    seen.add(c.id);
    return true;
  });
}

// -------------------------------------------------------------------------
// Reports
// -------------------------------------------------------------------------

/** Fetch (or return null for) the single daily_report for a child on a date.
 *  Nested entries are included sorted by occurred_at ASC. */
export async function fetchReport(childId, reportDate) {
  if (!isSupabaseConfigured) return demoGetReport(childId, reportDate);
  const { data, error } = await supabase
    .from("daily_reports")
    .select(
      `id, organization_id, child_id, classroom_id, report_date, overall_mood,
       summary_note, created_by, created_at, updated_at,
       entries:report_entries(
         id, daily_report_id, entry_type, occurred_at, detail, created_by, created_at
       )`,
    )
    .eq("child_id", childId)
    .eq("report_date", reportDate)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    ...data,
    entries: (data.entries || []).sort((a, b) =>
      a.occurred_at.localeCompare(b.occurred_at),
    ),
  };
}

export async function listReportsForChild(childId, limit = 30) {
  if (!isSupabaseConfigured) return demoListReportsForChild(childId);
  const { data, error } = await supabase
    .from("daily_reports")
    .select(
      `id, child_id, classroom_id, report_date, overall_mood, summary_note,
       created_by, created_at, updated_at,
       entries:report_entries(id, entry_type)`,
    )
    .eq("child_id", childId)
    .order("report_date", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

export async function listReportsForAdmin(filters = {}, limit = 100) {
  if (!isSupabaseConfigured) return demoListAllReports(filters);
  let query = supabase
    .from("daily_reports")
    .select(
      `id, child_id, classroom_id, report_date, overall_mood, summary_note,
       created_by, created_at, updated_at,
       child:children(id, first_name, last_name),
       classroom:classrooms(id, name),
       entries:report_entries(id, entry_type)`,
    )
    .order("report_date", { ascending: false })
    .order("updated_at", { ascending: false })
    .limit(limit);
  if (filters.classroomId) query = query.eq("classroom_id", filters.classroomId);
  if (filters.childId)     query = query.eq("child_id", filters.childId);
  if (filters.dateFrom)    query = query.gte("report_date", filters.dateFrom);
  if (filters.dateTo)      query = query.lte("report_date", filters.dateTo);
  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

/** Create the daily_report row if it doesn't exist, then return the full report. */
export async function upsertReport({
  childId,
  classroomId,
  reportDate,
  overallMood = null,
  summaryNote = null,
  createdBy,
  organizationId,
}) {
  if (!isSupabaseConfigured) {
    return demoUpsertReport({
      childId,
      classroomId,
      reportDate,
      overallMood,
      summaryNote,
      createdBy,
    });
  }
  const payload = {
    child_id: childId,
    classroom_id: classroomId,
    report_date: reportDate,
    organization_id: organizationId,
    created_by: createdBy,
  };
  if (overallMood !== null) payload.overall_mood = overallMood;
  if (summaryNote !== null) payload.summary_note = summaryNote;

  const { data, error } = await supabase
    .from("daily_reports")
    .upsert(payload, { onConflict: "child_id,report_date" })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function updateReportSummary({ reportId, overallMood, summaryNote }) {
  if (!isSupabaseConfigured) {
    // demo: find + update
    // Done in caller via demoUpsertReport path — the editor only calls this in live mode.
    return null;
  }
  const patch = {};
  if (overallMood !== undefined) patch.overall_mood = overallMood;
  if (summaryNote !== undefined) patch.summary_note = summaryNote;
  const { data, error } = await supabase
    .from("daily_reports")
    .update(patch)
    .eq("id", reportId)
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function addEntry({ report, entryType, detail, createdBy }) {
  if (!isSupabaseConfigured) {
    return demoAddEntry({ report, entryType, detail, createdBy });
  }
  const { data, error } = await supabase
    .from("report_entries")
    .insert({
      daily_report_id: report.id,
      entry_type: entryType,
      detail: detail || {},
      created_by: createdBy,
    })
    .select()
    .single();
  if (error) throw error;
  return data;
}

export async function deleteEntry({ report, entryId }) {
  if (!isSupabaseConfigured) {
    demoDeleteEntry({ report, entryId });
    return;
  }
  const { error } = await supabase
    .from("report_entries")
    .delete()
    .eq("id", entryId);
  if (error) throw error;
}

// Re-export demo fixtures that admin pages need for filter dropdowns when live
// Supabase isn't available.
export { DEMO_CLASSROOMS, DEMO_CHILDREN };
